import { Box, Chip, Tooltip } from '@mui/material';
import TooltipIconButton from '../../components/TooltipIconButton';
import NotesEditCell from './NotesEditCell';
import { Edit, WarningAmberRounded } from '@mui/icons-material';
import { GridColDef, GridRenderCellParams, GridRenderEditCellParams, GRID_DETAIL_PANEL_TOGGLE_FIELD } from '@mui/x-data-grid-premium';
import TagChips from '../../components/TagChips';
import type { ColumnChoice } from './ColumnsDialog';
import type { BreakdownEntry, BreakdownKey } from '../../services/tracking.service';
import {
  ENTITY_MODAL_NAMES,
  NIL_UUID,
  fmtMoney,
  fmtNum,
  fmtPct,
  fmtPct2,
  fmtRoas,
  formatDimValue,
  dimField,
  labelForField,
  levelDims,
} from './tracking-page.format';
import { ProfitDot, colorCell, CompareCell } from './tracking-page.cells';
import ActivityCell from '../../components/ActivityCell';
import { EST_COST_NOTE } from '../../constants/tracking-groupings';


export interface ConversionTypeColumn { field: string; name: string }

/** Shown as a header tooltip. Only for the metrics whose initials do not give the formula. */
/** Time buckets are not entities, so they have no "right now" to report. */

const METRIC_HELP: Record<string, string> = {
  profit: 'Revenue − cost.',
  roas: 'Return on ad spend — revenue ÷ cost. 1.0 is breakeven.',
  bot_visits: 'Visits identified as bots. Excluded from the other metrics unless bots are included.',
  conversions: 'Conversions (leads).',
  ctr: 'Click-through rate — clicks ÷ visits.',
  cv_rate: 'Conversion rate per visit — conversions ÷ visits.',
  cr: 'Conversion rate per click — conversions ÷ clicks.',
  epv: 'Earnings per visit — revenue ÷ visits.',
  cpv: 'Cost per visit — cost ÷ visits.',
  rpl: 'Revenue per lead — revenue ÷ conversions.',
  cpl: 'Cost per lead — cost ÷ conversions.',
};

const numCol = (col: Omit<GridColDef, 'type' | 'align' | 'headerAlign'>): GridColDef => ({
  type: 'number',
  align: 'right',
  headerAlign: 'right',
  description: METRIC_HELP[col.field],
  ...col,
});

// Cost-derived metrics — the ones that mislead when cost is apportioned.
const COST_DERIVED = new Set(['cost', 'profit', 'profit_delta', 'roas', 'cpv', 'cpl']);

const estimatedHeader = (headerName: string) => () => (
  <Tooltip title={EST_COST_NOTE}>
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {headerName}
      <WarningAmberRounded sx={{ fontSize: 14, color: 'warning.main' }} />
    </Box>
  </Tooltip>
);

const PROFIT_DOT_COL: GridColDef = {
  field: '_profitability',
  headerName: '',
  width: 36,
  minWidth: 24,   // MUI defaults minWidth to 50, which would override the width above
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  renderCell: (params: GridRenderCellParams) =>
    (params.row.id === '__totals__' ? null : <ProfitDot profit={Number(params.row.profit || 0)} />),
};

/**
 * The metrics, in report order. The drilldown shows the same numbers for a child
 * dimension, so both grids build from this — they used to carry identical copies.
 */
interface Metric {
  field: string;
  headerName: string;
  width: number;
  fmt: (n: number) => string;
  /** For a metric that colours its own value rather than just formatting it. */
  render?: (params: GridRenderCellParams) => React.ReactNode;
  /** Metrics where a fall is an improvement, so the arrow must not read as a loss. */
  goodDown?: boolean;
}

function metricColumns(
  conversionTypeColumns: ConversionTypeColumn[], compare = false, costEstimated = false,
): GridColDef[] {
  const metrics: Metric[] = [
    { field: 'cost', headerName: 'Cost', width: 100, fmt: fmtMoney, goodDown: true },
    { field: 'revenue', headerName: 'Revenue', width: 110, fmt: fmtMoney },
    { field: 'profit', headerName: 'Profit', width: 110, fmt: fmtMoney,
      render: (p) => colorCell(p.value, fmtMoney) },
    // Ranks by how much money moved, so a big swing on a big spender outranks a
    // total collapse on one spending $40.
    { field: 'profit_delta', headerName: 'Δ Profit', width: 100, fmt: fmtMoney,
      render: (p) => colorCell(p.value, fmtMoney) },
    // Colours against 1, not 0. ROI is still returned by the API and usable in rules.
    { field: 'roas', headerName: 'ROAS', width: 80, fmt: fmtRoas,
      render: (p) => colorCell(Number(p.value || 0) - 1, fmtRoas, p.value) },
    { field: 'visits', headerName: 'Visits', width: 80, fmt: fmtNum },
    { field: 'unique_visits', headerName: 'Uniq Visits', width: 95, fmt: fmtNum },
    { field: 'bot_visits', headerName: 'Bots', width: 70, fmt: fmtNum, goodDown: true },
    { field: 'clicks', headerName: 'Clicks', width: 80, fmt: fmtNum },
    { field: 'unique_clicks', headerName: 'Uniq Clicks', width: 95, fmt: fmtNum },
    ...conversionTypeColumns.map((ct) => ({
      field: ct.field, headerName: ct.name, width: 80, fmt: fmtNum,
    })),
    { field: 'conversions', headerName: 'CV', width: 70, fmt: fmtNum },
    { field: 'ctr', headerName: 'CTR', width: 75, fmt: fmtPct },
    { field: 'cv_rate', headerName: 'CV%', width: 75, fmt: fmtPct2 },
    { field: 'cr', headerName: 'CR', width: 75, fmt: fmtPct2 },
    { field: 'epv', headerName: 'EPV', width: 80, fmt: fmtMoney },
    { field: 'cpv', headerName: 'CPV', width: 80, fmt: fmtMoney, goodDown: true },
    { field: 'rpl', headerName: 'RPL', width: 80, fmt: fmtMoney },
    { field: 'cpl', headerName: 'CPL', width: 80, fmt: fmtMoney, goodDown: true },
  ];

  // The delta only means anything beside a comparison.
  return metrics.filter((m) => compare || m.field !== 'profit_delta').map((m) => {
    // Always a formatter, even when a renderer draws the cell: the grid prefers
    // renderCell, and the CSV export only ever reads valueFormatter. Without it Profit
    // and ROAS exported a bare number while every other money column exported "$1,234.50".
    const plain = {
      valueFormatter: (v: number) => m.fmt(v),
      ...(m.render ? { renderCell: m.render } : {}),
    };
    const estProps = costEstimated && COST_DERIVED.has(m.field)
      ? { renderHeader: estimatedHeader(m.headerName), description: EST_COST_NOTE }
      : {};
    if (!compare || m.field === 'profit_delta') {
      return numCol({ field: m.field, headerName: m.headerName, width: m.width, minWidth: 70, ...plain, ...estProps });
    }
    return numCol({
      field: m.field, headerName: m.headerName, width: m.width, minWidth: 70, ...estProps,
      // renderCell draws it; valueFormatter is what the CSV export reads, and without
      // one every number would export raw only while comparing.
      valueFormatter: (v: number) => m.fmt(v),
      renderCell: (p) => (
        <CompareCell value={p.value} prev={p.row[`prev_${m.field}`]} format={m.fmt} goodDown={m.goodDown}
          hint={p.row.id === '__totals__'
            ? 'The previous period, for the rows shown — not everything that ran then'
            : undefined}>
          {m.render ? m.render(p) : undefined}
        </CompareCell>
      ),
    });
  });
}

/** Which fields have a previous-period twin, taken from the metric list itself. */
const METRIC_FIELDS = new Set(
  metricColumns([], true).map((c) => c.field).filter((f) => f !== 'profit_delta'));

/**
 * The visible columns plus a previous-period twin for each metric, so exporting a
 * comparison carries the comparison. Screen columns are unchanged — the grid shows both
 * numbers in one cell, a spreadsheet needs two.
 */
export function exportColumns(columns: GridColDef[], compare: boolean): GridColDef[] {
  if (!compare) return columns;
  return columns.flatMap((c) => {
    const twin = METRIC_FIELDS.has(c.field) ? [{
      ...c, field: `prev_${c.field}`, headerName: `${c.headerName} (prev)`,
      renderCell: undefined,
    } as GridColDef] : [];
    return [c, ...twin];
  });
}

/** Every selectable column, in natural order, whether or not this grouping produces it. */
export function columnCatalogue(
  conversionTypeColumns: ConversionTypeColumn[], groupBy: string, comparing = false,
): ColumnChoice[] {
  const single = levelDims(groupBy).length <= 1;
  const contextual: ColumnChoice[] = [
    { field: '_tags', label: 'Tags', available: groupBy === 'flow_id' },
    { field: '_activity', label: 'Activity', available: !!ENTITY_MODAL_NAMES[groupBy] },
    { field: '_caps', label: 'Caps', available: CAPS_GROUP_BYS.has(groupBy) },
    { field: 'notes', label: 'Notes', available: single && !!ENTITY_MODAL_NAMES[groupBy] },
    { field: '_flows', label: 'Flows', available: single && groupBy !== 'flow_id' },
    { field: '_offers', label: 'Offers', available: single && groupBy !== 'offer_id' },
    { field: '_landing_pages', label: 'Landing Pages', available: single && groupBy !== 'landing_page_id' },
  ];
  // Spelled out here: beside Profit, the grid's compact "Δ Profit" reads as a second, flagged Profit.
  const metrics = metricColumns(conversionTypeColumns, true).map((c) => (c.field === 'profit_delta'
    ? { field: c.field, label: 'Profit change (vs previous period)', available: comparing }
    : { field: c.field, label: String(c.headerName || c.field), available: true }));
  return [...contextual, ...metrics];
}

export const CAPS_GROUP_BYS = new Set(['network_id', 'network_kind']);

function notesColumn(editable: boolean): GridColDef {
  return {
    field: 'notes', headerName: 'Notes', width: 200, minWidth: 100, sortable: false, filterable: false, editable,
    description: editable ? 'Double-click to edit. Enter saves, Shift+Enter adds a line, Esc cancels.' : undefined,
    renderEditCell: (params: GridRenderEditCellParams) => <NotesEditCell {...params} />,
    renderCell: (params: GridRenderCellParams) => (params.row.id === '__totals__' || !params.value ? null : (
      <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{String(params.value)}</Box>} placement="bottom-start">
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(params.value)}</Box>
      </Tooltip>
    )),
  };
}

function capsColumn(): GridColDef {
  return {
    field: '_caps', headerName: 'Caps', width: 150, sortable: false, filterable: false,
    description: 'Daily caps — spend / conversions / revenue. ∞ = no cap. Current config, not the date range.',
    renderCell: (params: GridRenderCellParams) => {
      if (params.row.id === '__totals__') return null;
      const c = params.row.caps as { spend: number; conversions: number; revenue: number } | null;
      if (!c) return null;
      const money = (n: number) => (n > 0 ? `$${n.toLocaleString()}` : '∞');
      const num = (n: number) => (n > 0 ? n.toLocaleString() : '∞');
      return (
        <Tooltip title="Daily caps — spend / conversions / revenue. ∞ = no cap.">
          <Box sx={{ display: 'flex', gap: 0.5, fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
            <span>{money(c.spend)}</span><Box component="span" sx={{ opacity: 0.4 }}>/</Box>
            <span>{num(c.conversions)}</span><Box component="span" sx={{ opacity: 0.4 }}>/</Box>
            <span>{money(c.revenue)}</span>
          </Box>
        </Tooltip>
      );
    },
  } as GridColDef;
}

function dimColumns(dims: string[], dimLabel: (dim: string) => string, opts: {
  narrow?: boolean; onEditEntity?: (field: string, id: string) => void;
}): GridColDef[] {
  return dims.map((dim, i) => ({
    field: dimField(dim),
    headerName: dimLabel(dim),
    width: opts.narrow ? 100 : 170,
    minWidth: 80,
    resizable: true,
    valueGetter: (_v, row) => {
      if (row.id === '__totals__') return i === 0 ? row.display_name : '';
      return row.names?.[dim] || formatDimValue(dim, row.keys?.[dim]);
    },
    cellClassName: () => 'group-name-cell',
    renderCell: (params: GridRenderCellParams) => {
      const id = params.row.keys?.[dim];
      const editable = !!opts.onEditEntity && !!ENTITY_MODAL_NAMES[dim]
        && params.row.id !== '__totals__' && !!id && id !== NIL_UUID;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, width: '100%' }}>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.value}</Box>
          {editable && (
            <TooltipIconButton
              className="row-edit-btn"
              size="small"
              title={`Edit ${dimLabel(dim)}`}
              onClick={(e) => { e.stopPropagation(); opts.onEditEntity!(dim, String(id)); }}
              sx={{ p: 0.25, ml: 'auto', flexShrink: 0 }}
            >
              <Edit sx={{ fontSize: 14 }} />
            </TooltipIconButton>
          )}
        </Box>
      );
    },
  } as GridColDef));
}

export function reportColumns({ groupBy, groupLabel, dimLabel = labelForField, conversionTypeColumns, flowTagMap, onEditEntity, narrow, compare, costEstimated, canEditNotes = false }: {
  groupBy: string;
  canEditNotes?: boolean;
  groupLabel: string;
  dimLabel?: (dim: string) => string;
  conversionTypeColumns: ConversionTypeColumn[];
  flowTagMap: Map<string, string[]>;
  onEditEntity: (field: string, id: string) => void;
  narrow?: boolean;
  compare?: boolean;
  costEstimated?: boolean;
}): GridColDef[] {
  const dims = levelDims(groupBy);
  if (dims.length > 1) {
    return [
      ...(narrow ? [{ field: GRID_DETAIL_PANEL_TOGGLE_FIELD, width: 30, minWidth: 30, resizable: false } as GridColDef] : []),
      PROFIT_DOT_COL,
      ...dimColumns(dims, dimLabel, { narrow, onEditEntity }),
      ...metricColumns(conversionTypeColumns, compare, costEstimated),
    ];
  }
  return [
    ...(narrow ? [{
      field: GRID_DETAIL_PANEL_TOGGLE_FIELD, width: 30, minWidth: 30, resizable: false,
    } as GridColDef] : []),
    PROFIT_DOT_COL,
    {
      field: 'display_name',
      headerName: groupLabel,
      width: narrow ? 100 : 220,
      minWidth: narrow ? 80 : 110,
      resizable: true,
      valueGetter: (_v, row) =>
        row.name || (row.id === '__totals__' ? row.group_key : formatDimValue(groupBy, row.group_key)),
      cellClassName: () => 'group-name-cell',
      renderCell: (params: GridRenderCellParams) => {
        const editable = !!ENTITY_MODAL_NAMES[groupBy]
          && params.row.id !== '__totals__'
          && !!params.row.group_key && params.row.group_key !== NIL_UUID;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, width: '100%' }}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.value}</Box>
            {editable && (
              <TooltipIconButton
                className="row-edit-btn"
                size="small"
                title={`Edit ${groupLabel}`}
                onClick={(e) => { e.stopPropagation(); onEditEntity(groupBy, String(params.row.group_key)); }}
                sx={{ p: 0.25, ml: 'auto', flexShrink: 0 }}
              >
                <Edit sx={{ fontSize: 14 }} />
              </TooltipIconButton>
            )}
          </Box>
        );
      },
    },
    // Campaign tags for the grouped flow — sits right after the grouping column.
    ...(groupBy === 'flow_id' ? [{
      field: '_tags', headerName: 'Tags', width: 170, sortable: false, filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        params.row.id === '__totals__' ? null : <TagChips ids={flowTagMap.get(String(params.row.group_key)) || []} />
      ),
    } as GridColDef] : []),
    // Entity-only: a country or a device cannot be alive or paused.
    ...(!ENTITY_MODAL_NAMES[groupBy] ? [] : [{
      field: '_activity', headerName: 'Activity', width: 120, sortable: false, filterable: false,
      description: "This entity's own non-bot visits in the last hour and 24 hours — not the "
        + "report's date range, and not narrowed by the row it sits under.",
      renderCell: (params: GridRenderCellParams) => (
        params.row.id === '__totals__' ? null : (
          <ActivityCell
            activity={params.row.activity}
            // groupBy, not ENTITY_MODAL_NAMES[groupBy]: that map is hyphenated for modal
            // names (traffic-source), and the server's kind is underscored.
            kind={groupBy.replace(/_id$/, '')}
            entityId={String(params.row.group_key)}
            updatedAt={params.row.activity_updated_at}
          />
        )
      ),
    } as GridColDef]),
    ...(CAPS_GROUP_BYS.has(groupBy) ? [capsColumn()] : []),
    ...(ENTITY_MODAL_NAMES[groupBy] ? [notesColumn(canEditNotes)] : []),
    // Dropped when the grouping IS that dimension — every chip would repeat the row name.
    ...(groupBy === 'flow_id' ? [] : [breakdownColumn('_flows', 'Flows', 'flows')]),
    ...(groupBy === 'offer_id' ? [] : [breakdownColumn('_offers', 'Offers', 'offers')]),
    ...(groupBy === 'landing_page_id' ? [] : [breakdownColumn('_landing_pages', 'Landing Pages', 'landing_pages')]),
    ...metricColumns(conversionTypeColumns, compare, costEstimated),
  ];
}

function breakdownColumn(field: string, headerName: string, key: BreakdownKey): GridColDef {
  return {
    field, headerName, width: 300, sortable: false, filterable: false,
    description: `Top ${key} behind each row — visits, and conversions where there are any.`,
    renderCell: (params: GridRenderCellParams) => {
      if (params.row.id === '__totals__') return null;
      const all: BreakdownEntry[] = params.row._breakdown?.[key] || [];
      if (!all.length) return null;
      // A third chip at this width squeezes the names to nothing.
      const shown = all.slice(0, 2);
      const rest = all.length - shown.length;
      return (
        <Box sx={{ display: 'flex', gap: 0.5, overflow: 'hidden', width: '100%', alignItems: 'center' }}>
          {shown.map((it) => (
            <Tooltip
              key={it.id}
              title={`${it.name || it.id} — ${it.visits.toLocaleString()} visits, ${it.conversions.toLocaleString()} conversions`}
            >
              <Chip
                size="small"
                sx={{ minWidth: 0, '& .MuiChip-label': { display: 'flex', gap: 0.5, minWidth: 0 } }}
                label={(
                  <>
                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 52 }}>
                      {it.name || it.id}
                    </Box>
                    <Box component="span" sx={{ flexShrink: 0, fontWeight: 600 }}>
                      {it.visits.toLocaleString()}
                      {it.conversions > 0 && (
                        <Box component="span" sx={{ color: 'success.main' }}>{` · ${it.conversions}`}</Box>
                      )}
                    </Box>
                  </>
                )}
              />
            </Tooltip>
          ))}
          {rest > 0 && (
            <Tooltip title={all.slice(2).map((it) => `${it.name || it.id} — ${it.visits.toLocaleString()}`).join('\n')}>
              <Chip size="small" variant="outlined" label={`+${rest}`} sx={{ flexShrink: 0 }} />
            </Tooltip>
          )}
        </Box>
      );
    },
  };
}

export function drilldownColumns({ childGroupBy, childLabel, dimLabel = labelForField, conversionTypeColumns, compare, costEstimated, canEditNotes = false }: {
  childGroupBy: string;
  canEditNotes?: boolean;
  childLabel: string;
  dimLabel?: (dim: string) => string;
  conversionTypeColumns: ConversionTypeColumn[];
  compare?: boolean;
  costEstimated?: boolean;
}): GridColDef[] {
  const dims = levelDims(childGroupBy);
  if (dims.length > 1) {
    return [
      PROFIT_DOT_COL,
      ...dimColumns(dims, dimLabel, {}),
      ...metricColumns(conversionTypeColumns, compare, costEstimated),
    ];
  }
  return [
    PROFIT_DOT_COL,
    {
      field: 'display_name', headerName: childLabel, width: 220, minWidth: 110, resizable: true,
      valueGetter: (_v, row) => row.name || formatDimValue(childGroupBy, row.group_key),
      cellClassName: () => 'group-name-cell',
    },
    ...(!ENTITY_MODAL_NAMES[childGroupBy] ? [] : [{
      field: '_activity', headerName: 'Activity', width: 120, sortable: false, filterable: false,
      description: "This entity's own non-bot visits in the last hour and 24 hours — not the "
        + "report's date range, and not narrowed by the row it sits under.",
      renderCell: (params: GridRenderCellParams) => (
        <ActivityCell
          activity={params.row.activity}
          kind={childGroupBy.replace(/_id$/, '')}
          entityId={String(params.row.group_key)}
          updatedAt={params.row.activity_updated_at}
        />
      ),
    } as GridColDef]),
    ...(CAPS_GROUP_BYS.has(childGroupBy) ? [capsColumn()] : []),
    ...(ENTITY_MODAL_NAMES[childGroupBy] ? [notesColumn(canEditNotes)] : []),
    ...metricColumns(conversionTypeColumns, compare, costEstimated),
  ];
}
